import {
  Checkbox,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useFieldArray, useFormContext } from "react-hook-form";
import SectionHeader from "@quest_editor/components/SectionHeader";
import { DayOfWeekValue, daysOfWeek } from "@quest_editor/constants";

type NotificationSettingsFormData = {
  rows: { email: string; days: DayOfWeekValue[] }[];
};

type ServerError = {
  index: number;
  message: string;
};

type Props = {
  serverErrors?: ServerError[];
};

const NotificationSettingsSection = ({ serverErrors = [] }: Props) => {
  const { t } = useTranslation("questEditor");
  const { register, control, watch, setValue } =
    useFormContext<NotificationSettingsFormData>();
  const { fields } = useFieldArray({ control, name: "rows" });

  const serverErrorMap = new Map(
    serverErrors.map((e) => [e.index, e.message]),
  );

  const handleToggleDay = (
    index: number,
    day: DayOfWeekValue,
    currentDays: DayOfWeekValue[],
  ) => {
    const checked = currentDays.includes(day);
    const newDays = checked
      ? currentDays.filter((d) => d !== day)
      : [...currentDays, day];
    setValue(`rows.${index}.days`, newDays);
  };

  return (
    <Paper elevation={0} sx={{ border: 1, borderColor: "divider" }}>
      <SectionHeader
        title={t("promotion_progress.section.notification_settings")}
      />
      <TableContainer>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ minWidth: 250 }}>
                {t("promotion_progress.notification.email")}
              </TableCell>
              {daysOfWeek.map((day) => (
                <TableCell key={day} align="center" sx={{ minWidth: 50 }}>
                  {t(`promotion_progress.notification.${day}`)}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {fields.map((field, index) => {
              const hasServerError = serverErrorMap.has(index);
              const serverErrorMessage = serverErrorMap.get(index);
              const currentDays = watch(`rows.${index}.days`);

              return (
                <TableRow
                  key={field.id}
                  sx={
                    hasServerError
                      ? {
                          bgcolor: "#ffebee",
                          "& .MuiTableCell-root": {
                            bgcolor: "#ffebee",
                          },
                        }
                      : undefined
                  }
                >
                  <TableCell>
                    <TextField
                      size="small"
                      fullWidth
                      {...register(`rows.${index}.email`)}
                      error={hasServerError}
                      helperText={
                        hasServerError ? serverErrorMessage : undefined
                      }
                    />
                  </TableCell>
                  {daysOfWeek.map((day) => (
                    <TableCell key={day} align="center" padding="checkbox">
                      <Checkbox
                        size="small"
                        checked={currentDays.includes(day)}
                        onChange={() =>
                          handleToggleDay(index, day, currentDays)
                        }
                      />
                    </TableCell>
                  ))}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default NotificationSettingsSection;
